import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import * as actions from "../../../ducks/app/file-uploads";
import { useAppDispatch } from "../../../redux-store";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";
import { isMobile } from "../../../helpers/mobile";
import { Filesystem, Directory } from "@capacitor/filesystem";
import useFeature from "../../../hooks/useFeature";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useParams } from "../hooks";
import LoadingButton from "../../../components/LoadingButton";
import useShowUploadError from "./useShowUploadError";
import * as Sentry from "@sentry/capacitor";
import { EmptyFileError, toError } from "../../../helpers/actionError";

const AddImage = ({
  cargoId,
  stopId,
  added,
  imagesForStop,
  onSuccess,
}: {
  cargoId: string;
  stopId: string;
  added: boolean;
  imagesForStop: number;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation(["stopPage", "translation"]);
  const params = useParams();
  const truckId = params.truckId as string;
  const useActivitiesEndpoints = useFeature("driver-activities-endpoints");
  const [deniedCameraAccessError, setDeniedCameraAccessError] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const convertHeicToJpeg = useFeature("mobile-convert-heic-to-jpeg");
  const showUploadError = useShowUploadError();

  const uploadPhotoNewMobile = async (input: {
    image: {
      base64String: string;
      name: string;
    };
    truckId: string;
  }) => {
    const { image, truckId } = input;
    setLoading(true);
    const tempFileName = `temp-upload-${Date.now()}.jpeg`;
    let wroteTempFile = false;
    try {
      let writeResult;
      try {
        writeResult = await Filesystem.writeFile({
          path: tempFileName,
          data: image.base64String,
          directory: Directory.Cache,
        });
      } catch (err) {
        showUploadError(err, { reported: false });
        return;
      }
      wroteTempFile = true;
      const s3Identifier = await dispatch(
        actions.uploadFileToS3Mobile({
          contentType: "image/jpeg",
          path: writeResult.uri,
        })
      ).unwrap();
      await dispatch(
        actions.addFileReference({
          truckId,
          cargoId,
          stopId,
          file: {
            name: image.name,
            mimeType: "image/jpeg",
            documentType: "POD",
            s3Identifier,
          },
        })
      ).unwrap();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      showUploadError(err, { reported: true });
    } finally {
      setLoading(false);
      // Cache cleanup; not awaited so it can't hold up the driver, and a
      // failure is only reported (it means temp files pile up in the cache).
      if (wroteTempFile) {
        Filesystem.deleteFile({
          path: tempFileName,
          directory: Directory.Cache,
        }).catch((err) => {
          Sentry.captureException(toError(err), {
            extra: { type: "Temp photo file delete failed", tempFileName },
          });
        });
      }
    }
  };

  const uploadPhotoNewMobileUri = async (input: {
    image: {
      path: string;
      name: string;
      fileType: string;
    };
    truckId: string;
    useActivitiesEndpoints: boolean;
  }) => {
    const { image, truckId, useActivitiesEndpoints } = input;
    setLoading(true);
    try {
      const s3Identifier = await dispatch(
        actions.uploadFileToS3Mobile({
          contentType: "image/jpeg",
          path: image.path,
        })
      ).unwrap();

      if (useActivitiesEndpoints) {
        await dispatch(
          actions.addFileReference({
            truckId,
            cargoId,
            stopId,
            file: {
              name: image.name,
              mimeType: "image/jpeg",
              documentType: "POD",
              s3Identifier,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          actions.addFileReference({
            cargoId,
            truckId,
            stopId,
            file: {
              name: image.name,
              mimeType: image.fileType || "image/jpeg",
              documentType: "POD",
              s3Identifier,
            },
          })
        ).unwrap();
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      showUploadError(err, { reported: true });
    } finally {
      setLoading(false);
    }
  };

  const uploadPhotoNewWeb = async (input: { file: File; truckId: string }) => {
    const { file, truckId } = input;
    if (file.size === 0) {
      showUploadError(new EmptyFileError(), { reported: false });
      return;
    }
    setLoading(true);
    try {
      const urlResult = await dispatch(
        actions.createFileUploadURL({ contentType: "image/jpeg" })
      ).unwrap();
      await dispatch(
        actions.uploadFileToS3Web({
          uploadUrl: urlResult.uploadUrl,
          contentType: "image/jpeg",
          file,
        })
      ).unwrap();

      await dispatch(
        actions.addFileReference({
          truckId,
          cargoId,
          stopId,
          file: {
            name: file.name,
            mimeType: "image/jpeg",
            documentType: "POD",
            s3Identifier: urlResult.s3Identifier,
          },
        })
      ).unwrap();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      showUploadError(err, { reported: true });
    } finally {
      setLoading(false);
    }
  };

  const checkAndRequestPermissions = async (
    source: CameraSource.Camera | CameraSource.Photos
  ): Promise<boolean> => {
    const permissions = await Camera.checkPermissions();
    const relevantPermission =
      source === CameraSource.Camera ? permissions.camera : permissions.photos;

    if (
      relevantPermission === "prompt" ||
      relevantPermission === "prompt-with-rationale"
    ) {
      const newPermissions = await Camera.requestPermissions();
      const newRelevantPermission =
        source === CameraSource.Camera
          ? newPermissions.camera
          : newPermissions.photos;
      return newRelevantPermission === "granted";
    } else if (relevantPermission === "denied") {
      setDeniedCameraAccessError(true);
      return false;
    }

    return relevantPermission === "granted";
  };

  const getPhoto = async (
    source: CameraSource.Camera | CameraSource.Photos,
    resultType: CameraResultType
  ) => {
    try {
      const hasPermission = await checkAndRequestPermissions(source);
      if (!hasPermission) {
        return null;
      }
      return await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType,
        source,
      });
    } catch (err: any) {
      if (err.message === "User denied access to camera") {
        setDeniedCameraAccessError(true);
      } else if (err.message === "User cancelled photos app") {
        console.log("User cancelled photos app — ignoring");
      } else {
        showUploadError(err, { reported: false });
      }
      return null;
    }
  };

  const takePhoto = async (
    source: CameraSource.Camera | CameraSource.Photos
  ) => {
    if (convertHeicToJpeg) {
      const cameraPhoto = await getPhoto(source, CameraResultType.Base64);
      if (!cameraPhoto?.base64String) {
        return;
      }
      const fileName = `mobile-upload-${new Date().toISOString()}.jpeg`;
      await uploadPhotoNewMobile({
        image: {
          base64String: cameraPhoto.base64String,
          name: fileName,
        },
        truckId: truckId,
      });
    } else {
      const cameraPhotoUri = await getPhoto(source, CameraResultType.Uri);
      if (!cameraPhotoUri) {
        return;
      }
      const fileName = `mobile-upload-${new Date().toISOString()}.${cameraPhotoUri.format}`;
      let fileType = cameraPhotoUri.format;
      if (fileType === "jpeg") {
        fileType = "image/jpeg";
      } else if (fileType === "png") {
        fileType = "image/png";
      }
      await uploadPhotoNewMobileUri({
        image: {
          path: cameraPhotoUri.path!,
          name: fileName,
          fileType,
        },
        truckId: truckId,
        useActivitiesEndpoints,
      });
    }
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) {
      return;
    }
    await uploadPhotoNewWeb({
      file: files[0],
      truckId,
    });
  };

  return (
    <>
      {deniedCameraAccessError && (
        <Dialog open onClose={() => setDeniedCameraAccessError(false)}>
          <DialogTitle>{t("photosPermissionsDialog.title")}</DialogTitle>
          <DialogContent>{t("photosPermissionsDialog.body")}</DialogContent>
          <DialogActions>
            <div className="flex gap-2 justify-center w-full mt-4">
              <Button onClick={() => setDeniedCameraAccessError(false)}>
                {t("photosPermissionsDialog.buttonClose")}
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  await NativeSettings.open({
                    optionIOS: IOSSettings.App,
                    optionAndroid: AndroidSettings.Application,
                  });
                  setDeniedCameraAccessError(false);
                }}
              >
                {t("photosPermissionsDialog.buttonOpenSettings")}
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      )}
      <form onSubmit={(e) => e.preventDefault()}>
        <label>
          <LoadingButton
            loading={loading}
            variant="outlined"
            fullWidth
            size="large"
            component="span"
            startIcon={<CameraAltOutlinedIcon />}
            onClick={() => {
              if (isMobile) {
                takePhoto(CameraSource.Camera);
              }
            }}
          >
            {t("takePhoto")}
            {!isMobile && (
              <input
                capture="environment"
                style={{ display: "none" }}
                type="file"
                id="take-picture"
                accept="image/*"
                onChange={onChange}
              />
            )}
            {added && <CheckIcon />}
            <span>{imagesForStop ? `(${imagesForStop})` : ""}</span>
          </LoadingButton>
        </label>
      </form>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <label>
          <LoadingButton
            loading={loading}
            variant="outlined"
            fullWidth
            size="large"
            component="span"
            startIcon={<CollectionsOutlinedIcon />}
            onClick={() => {
              if (isMobile) {
                takePhoto(CameraSource.Photos);
              }
            }}
          >
            <span>{t("addPhoto")}</span>
            {!isMobile && (
              <input
                style={{ display: "none" }}
                type="file"
                id="take-picture"
                accept="image/*"
                onChange={onChange}
              />
            )}
            {added && <CheckIcon />}
          </LoadingButton>
        </label>
      </form>
    </>
  );
};

export default AddImage;
